import {
  Box,
  Code,
  ListItem,
  OrderedList,
  Text,
  UnorderedList,
  useColorModeValue,
} from '@chakra-ui/react';
import type { Components } from 'react-markdown';
import ReactMarkdown from 'react-markdown';
import {
  METHODOLOGY_BODY_FONT_SIZE,
  METHODOLOGY_COMPACT_FONT_SIZE,
  METHODOLOGY_FONT_FAMILY,
  METHODOLOGY_SECTION_TITLE_FONT_SIZE,
} from '@/constants/aiLeague/methodologyTypography';

export function AiLeagueMarkdown(props: { content: string; compact?: boolean }) {
  const textColor = useColorModeValue('gray.800', 'gray.100');
  const mutedColor = useColorModeValue('gray.600', 'gray.300');
  const codeBg = useColorModeValue('blackAlpha.50', 'whiteAlpha.100');
  const fontSize = props.compact ? METHODOLOGY_COMPACT_FONT_SIZE : METHODOLOGY_BODY_FONT_SIZE;
  const headingFontSize = props.compact
    ? METHODOLOGY_BODY_FONT_SIZE
    : METHODOLOGY_SECTION_TITLE_FONT_SIZE;
  const subheadingFontSize = props.compact ? METHODOLOGY_COMPACT_FONT_SIZE : METHODOLOGY_BODY_FONT_SIZE;

  const components: Components = {
    h1: ({ children }) => (
      <Text
        as='h3'
        fontSize={headingFontSize}
        fontWeight='semibold'
        mt={4}
        mb={2}
        fontFamily={METHODOLOGY_FONT_FAMILY}
        textAlign='left'
      >
        {children}
      </Text>
    ),
    h2: ({ children }) => (
      <Text
        as='h4'
        fontSize={subheadingFontSize}
        fontWeight='semibold'
        mt={4}
        mb={2}
        fontFamily={METHODOLOGY_FONT_FAMILY}
        textAlign='left'
      >
        {children}
      </Text>
    ),
    h3: ({ children }) => (
      <Text
        as='p'
        fontWeight='semibold'
        fontSize={fontSize}
        mt={3}
        mb={1}
        fontFamily={METHODOLOGY_FONT_FAMILY}
        textAlign='left'
      >
        {children}
      </Text>
    ),
    p: ({ children }) => (
      <Text
        fontSize={fontSize}
        lineHeight='1.75'
        mb={2}
        color={textColor}
        fontFamily={METHODOLOGY_FONT_FAMILY}
        textAlign='left'
      >
        {children}
      </Text>
    ),
    strong: ({ children }) => (
      <Text as='span' fontWeight='semibold' fontFamily={METHODOLOGY_FONT_FAMILY}>
        {children}
      </Text>
    ),
    ul: ({ children }) => (
      <UnorderedList
        spacing={1}
        pl={5}
        mb={3}
        fontSize={fontSize}
        color={textColor}
        fontFamily={METHODOLOGY_FONT_FAMILY}
        textAlign='left'
      >
        {children}
      </UnorderedList>
    ),
    ol: ({ children }) => (
      <OrderedList
        spacing={1}
        pl={5}
        mb={3}
        fontSize={fontSize}
        color={textColor}
        fontFamily={METHODOLOGY_FONT_FAMILY}
        textAlign='left'
      >
        {children}
      </OrderedList>
    ),
    li: ({ children }) => <ListItem textAlign='left'>{children}</ListItem>,
    code: ({ className, children }) => {
      const isBlock = Boolean(className) || String(children).includes('\n');
      if (isBlock) {
        return (
          <Box
            as='pre'
            fontSize={METHODOLOGY_COMPACT_FONT_SIZE}
            lineHeight='1.6'
            p={3}
            mb={3}
            borderRadius='md'
            bg={codeBg}
            overflowX='auto'
            fontFamily='mono'
            textAlign='left'
            whiteSpace='pre-wrap'
          >
            <Code display='block' bg='transparent' p={0} fontSize='inherit'>
              {children}
            </Code>
          </Box>
        );
      }
      return (
        <Code fontSize={METHODOLOGY_COMPACT_FONT_SIZE} px={1} bg={codeBg} fontFamily='mono'>
          {children}
        </Code>
      );
    },
    a: ({ href, children }) => (
      <Text
        as='a'
        href={href}
        color='blue.500'
        textDecoration='underline'
        fontFamily={METHODOLOGY_FONT_FAMILY}
      >
        {children}
      </Text>
    ),
    hr: () => <Box as='hr' my={4} borderColor={mutedColor} />,
  };

  return (
    <Box w='full' textAlign='left'>
      <ReactMarkdown components={components}>{props.content}</ReactMarkdown>
    </Box>
  );
}
